/**
 * The key that makes a write safe to repeat.
 *
 * Every route the API marks `@Idempotent` reads an `Idempotency-Key` header and
 * **refuses the request outright without one** — `SYNC_IDEMPOTENCY_KEY_MISSING`,
 * a 400, before the transaction opens. It is not an optimisation a caller may
 * skip; it is part of the request.
 *
 * What it buys is the answer to the only question that matters after a dropped
 * response: *did that go through?* The server claims the key, runs the handler
 * and records the response inside one transaction, so a second request carrying
 * the same key is answered with the first one's response rather than performing
 * the work again. Registration is the case this was built for — the response
 * carries the lease that authorises a till, and a retry that minted a fresh key
 * would issue a second lease and leave the device holding a superseded one.
 *
 * So: **one key per attempt, reused by every retry of that attempt.** A new key
 * means "this is a different piece of work", and on this route that is a
 * statement with consequences.
 */

/** The header the API reads the key from. Lower-cased by the transport. */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';

/**
 * A fresh key.
 *
 * Not injected the way `lib/clock.ts` is, and the difference is deliberate. A
 * clock is injected because behaviour depends on the *value* it returns — an
 * expiry either has passed or has not, and a test has to pin which. Nothing
 * here depends on the value: the key is opaque to this app, and what a test
 * asserts is that two requests carried the *same* one, which it can see without
 * knowing what it was. Wiring a generator through the tree to satisfy the
 * letter of the rule would add a seam with nothing on either side of it.
 *
 * `crypto.randomUUID` needs a secure context. The back office is served over
 * HTTPS and the desktop shell registers `app://` with `secure: true`, so both
 * targets have it; a bare `http://` origin other than localhost would not, and
 * is not a way this app is served.
 */
export const newIdempotencyKey = (): string => crypto.randomUUID();
